import {
	Component,
	OnInit,
	HostListener,
	ViewChild,
	ElementRef,
	output,
	inject,
	signal,
} from '@angular/core'
import { SearchInputComponent } from '../../components/search-input/search-input.component'
import { ActionRegistryService } from '../../services/action-registry.service'
import { BaseAction } from '../../models/base-action.model'
import { SearchQuery } from '../../models/search-query.model'
import { first } from 'rxjs'

@Component({
	selector: 'lowgile-quick-actions-container',
	templateUrl: './quick-actions-container.component.html',
	styleUrls: ['./quick-actions-container.component.scss'],
	standalone: false,
})
export class QuickActionsContainerComponent implements OnInit {
	@ViewChild('overlay') overlayRef: ElementRef<HTMLDivElement>
	@ViewChild('container') containerRef: ElementRef<HTMLDivElement>
	@ViewChild(SearchInputComponent) searchInputComponent: SearchInputComponent

	close = output<void>()
	actionExecute = output<BaseAction>()

	private actionRegistryService = inject(ActionRegistryService)

	isVisible = signal(false)
	activeIndex = signal(0)
	currentQuery = signal<SearchQuery | null>(null)
	prefixHelper = signal('')

	filteredActions = signal<BaseAction[]>([])

	constructor() {}

	ngOnInit(): void {}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.isVisible()) {
			this.hide()
		}
	}

	@HostListener('window:keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (
			(event.metaKey && event.key === 'p') ||
			(event.ctrlKey && event.key === 'p')
		) {
			event.preventDefault()
			event.stopPropagation()
		}
	}

	@HostListener('document:keydown.meta.p', ['$event'])
	@HostListener('document:keydown.control.p', ['$event'])
	onToggleShortcut(event: KeyboardEvent): void {
		event.preventDefault()
		this.toggle()
	}

	@HostListener('document:keydown.arrowdown', ['$event'])
	onArrowDown(event: KeyboardEvent): void {
		if (!this.isVisible()) return
		event.preventDefault()

		const actions = this.filteredActions()
		if (actions.length === 0) return

		this.activeIndex.set((this.activeIndex() + 1) % actions.length)
	}

	@HostListener('document:keydown.arrowup', ['$event'])
	onArrowUp(event: KeyboardEvent): void {
		if (!this.isVisible()) return
		event.preventDefault()

		const actions = this.filteredActions()
		if (actions.length === 0) return

		this.activeIndex.set(
			this.activeIndex() === 0 ? actions.length - 1 : this.activeIndex() - 1,
		)
	}

	@HostListener('document:keydown.enter', ['$event'])
	onEnter(event: KeyboardEvent): void {
		if (!this.isVisible()) return
		event.preventDefault()

		const actions = this.filteredActions()
		if (actions.length === 0) return

		const selectedAction = actions[this.activeIndex()]
		if (selectedAction) {
			this.onActionSelect(selectedAction)
		}
	}

	show(): void {
		this.isVisible.set(true)

		this.activeIndex.set(0)
		this.filterActions({ rawQuery: '', prefix: null, query: '' })

		setTimeout(() => {
			this.searchInputComponent?.focus()
		}, 100)
	}

	hide(): void {
		this.isVisible.set(false)
		this.searchInputComponent?.clear()
		this.filteredActions.set([])
		this.currentQuery.set(null)
		this.close.emit()
	}

	toggle(): void {
		if (this.isVisible()) {
			this.hide()
		} else {
			this.show()
		}
	}

	onOverlayClick(event: MouseEvent): void {
		if (event.target === this.overlayRef.nativeElement) {
			this.hide()
		}
	}

	onSearchQueryChange(query: SearchQuery): void {
		if (!query.rawQuery) {
			this.filteredActions.set([])
		}

		this.currentQuery.set(query)
		this.activeIndex.set(0)

		this.filterActions(query)
	}

	onActionSelect(action: BaseAction): void {
		if (action.canExecute()) {
			action.execute()
		}

		this.actionExecute.emit(action)

		this.hide()
	}

	private filterActions(query: SearchQuery): void {
		const searchTerm = query.query.toLowerCase()
		const prefix = query.prefix ?? ''

		if (!searchTerm) {
			this.actionRegistryService
				.getProviderHelperByPrefix(prefix)
				.pipe(first())
				.subscribe((helper) => {
					this.prefixHelper.set(helper)
				})

			return
		}

		this.prefixHelper.set('')

		this.actionRegistryService
			.searchByPrefix(prefix, searchTerm)
			.pipe(first())
			.subscribe((actions) => {
				this.filteredActions.set(actions)
			})
	}
}
